<template>
  <article class="sc">
    <div class="sc-head">
      <span class="sc-icon" v-if="icon"><AppIcon :name="icon" /></span>
      <span class="sc-label">{{ label }}</span>
      <span class="sc-delta" :class="'is-' + (deltaTone || 'flat')" v-if="deltaText">{{ deltaText }}</span>
    </div>
    <span class="sc-value num">{{ display }}</span>
    <span class="sc-meta" v-if="meta">{{ meta }}</span>
    <div class="sc-spark" :class="'is-' + (sparkTone || 'flat')" v-if="spark && spark.length > 1">
      <SparkLine :values="spark" :width="160" :height="30" :label="label" />
    </div>
    <slot />
  </article>
</template>

<script>
import { formatCurrency } from "@/views/Analytics/utils";

/**
 * One number, its name, and what it did, in the panel's KPI look (surfaces.css
 * .ui-kpi): an optional icon tile, the label, the change as a tinted pill, the
 * value large underneath.
 *
 * `tone` on the delta and the sparkline carries meaning (up = good, down = bad);
 * the caller decides which, because "orders down" and "discounts down" are not
 * the same news.
 */
export default {
  name: "StatCard",
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: "" },
    value: { type: [Number, String], default: 0 },
    format: { type: String, default: "money" }, // money | int | text
    meta: { type: String, default: "" },
    deltaText: { type: String, default: "" },
    deltaTone: { type: String, default: "" }, // up | down | ''
    spark: { type: Array, default: null },
    sparkTone: { type: String, default: "" },
  },
  computed: {
    numeric() { return this.format !== "text" && typeof this.value !== "string"; },
    display() {
      if (!this.numeric) return this.value;
      if (this.format === "int") return Math.round(Number(this.value) || 0).toLocaleString();
      return formatCurrency(Number(this.value) || 0);
    },
  },
};
</script>

<style scoped>
.sc {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-5);
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: var(--card-radius, 16px);
  box-shadow: var(--card-shadow);
  min-width: 0;
}
.sc-head { display: flex; flex-wrap: wrap; align-items: center; gap: var(--s-2) var(--s-3); }
.sc-icon {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-orange);
  background: var(--accent-primary-bg);
}
.sc-icon :deep(svg) { width: 20px; height: 20px; }
.sc-label {
  flex: 1 1 8rem;
  min-width: 0;
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  font-weight: var(--w-semibold);
  letter-spacing: var(--track-caps);
  text-transform: uppercase;
  color: var(--text-muted);
}
.sc-value {
  margin-top: var(--s-2);
  font-size: var(--t-metric);
  line-height: var(--t-metric-lh);
  letter-spacing: var(--track-tight);
  font-weight: var(--w-bold);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
.sc-meta { font-size: var(--t-body); line-height: var(--t-body-lh); color: var(--text-muted); }
.sc-delta {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  font-weight: var(--w-semibold);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  padding: 1px var(--s-2);
  border-radius: 999px;
  background: var(--bg-tertiary);
}
.sc-delta.is-up { color: var(--success); background: var(--success-bg); }
.sc-delta.is-down { color: var(--danger); background: var(--danger-bg); }
.sc-spark { color: var(--text-muted); }
.sc-spark.is-up { color: var(--success); }
.sc-spark.is-down { color: var(--danger); }
</style>
